import { useFormat, useLocale, useTranslation } from '@dreamstack/i18n'
import { assertPopulated } from '@dreamstack/payload/src/lib/assertPopulated'
import { NewsArticle } from '@dreamstack/payload/src/payload-types'
import { RouterOutputs } from '@dreamstack/trpc/client'
import type { FunctionComponent } from 'react'
import type { NewsType } from './newsTypeUrls.static'
import { HeadingH3, HeadingH4 } from '../layout/Texts'
import { ContinueReadingLink } from './ContinueReadingLink'
import { PageTag } from '../layout/PageTag'
import { getLexiconPath } from '../lexicon/getLexiconPath'
import { getNewsPath } from './getNewsPath'

const FeaturedLexiconArticle = ({
  article,
  href,
}: {
  article:
    | RouterOutputs['cms']['getLexiconArticle']['article']
    | RouterOutputs['cms']['getLexiconArticle']['otherArticles'][number]
  href: string
}) => {
  return (
    <div className="md:pr-px-64">
      <div className="mt-px-48 md:mt-px-64 lg:mt-px-80">
        <HeadingH3
          className="mt-px-24 lg:mt-px-32 hyphens-auto"
          dangerouslySetInnerHTML={{ __html: article.title ?? '' }}
        />
      </div>
      <div className="mt-px-24 lg:mt-px-32">{article.previewText}</div>
      <ContinueReadingLink href={href} />
    </div>
  )
}

const FeaturedNewsArticle = ({
  newsType,
  article,
  href,
  date,
}: {
  newsType: NewsType | string
  article: NewsArticle
  href: string
  date: string | undefined | null
}) => {
  assertPopulated(article)
  assertPopulated(article.file)
  return (
    <div className="md:pr-px-64">
      <div className="mt-px-48 md:mt-px-64 lg:mt-px-80">
        <PageTag text={newsType}></PageTag>
      </div>
      <HeadingH3
        className="mt-px-24 lg:mt-px-32 hyphens-auto"
        dangerouslySetInnerHTML={{ __html: article.title ?? '' }}
      />
      {date && !article.hideDate && (
        <HeadingH4 className="mt-px-16">{date}</HeadingH4>
      )}
      <div className="mt-px-24 lg:mt-px-32">{article.previewText}</div>
      <ContinueReadingLink href={href} file={article.file} />
    </div>
  )
}

export const FeaturedArticleTemplate: FunctionComponent<
  React.PropsWithChildren<
    | {
        newsArticle: NewsArticle
        lexiconArticle?: never
      }
    | {
        newsArticle?: never
        lexiconArticle:
          | RouterOutputs['cms']['getLexiconArticle']['article']
          | RouterOutputs['cms']['getLexiconArticle']['otherArticles'][number]
      }
  >
> = ({ newsArticle, lexiconArticle }) => {
  const t = useTranslation()
  const formatDate = useFormat()
  const locale = useLocale()

  const slug = newsArticle?.slug ?? lexiconArticle?.slug
  if (!slug) return null

  if (lexiconArticle) {
    const { lexiconLetter } = lexiconArticle
    if (!lexiconLetter) return null
    const lexiconPath = getLexiconPath({ locale, slug, lexiconLetter })
    return (
      <FeaturedLexiconArticle article={lexiconArticle} href={lexiconPath} />
    )
  }

  if (newsArticle) {
    const newsType: NewsType = newsArticle?.newsType as NewsType //newsType is set in contentful and should be a valid NewsType
    const translatedNewsType = t(`accentro:newsTypes.${newsArticle?.newsType}`)

    const formattedOtherArticlesDate = newsArticle.date
      ? formatDate(newsArticle.date, 'dd. MMMM yyyy')
      : null

    const newsPath = getNewsPath({ locale, slug, newsType })

    return (
      <FeaturedNewsArticle
        newsType={translatedNewsType}
        article={newsArticle}
        date={formattedOtherArticlesDate}
        href={newsPath}
      />
    )
  }
  return null
}
